import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Box, Collapse, TextField, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { updateCampaignNotes } from 'src/apis/campaign';
import Button from 'src/components/Button';
import Spinner from 'src/components/Spinner';
import useNotification from 'src/hooks/useNotification';
import { CampaignDetailProps } from 'src/types/campaign';

const NOTES_MAX_LENGTH = 5000;

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      border: '1px solid #EAECF0',
      borderRadius: 8,
      overflow: 'hidden',
      background: '#fff',
      width: '100%',
    },
    header: {
      padding: '12px 16px',
      background: '#F9FAFB',
      fontWeight: 600,
      fontSize: 14,
      color: '#344054',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&:hover': { background: '#F2F4F7' },
    },
    headerOpen: {
      borderBottom: '1px solid #EAECF0',
    },
    body: {
      padding: 16,
    },
    notes: {
      fontSize: 14,
      lineHeight: 1.5,
      color: '#101828',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    empty: {
      fontSize: 14,
      color: '#98A2B3',
      fontStyle: 'italic',
    },
    hint: {
      fontSize: 12,
      color: '#667085',
      marginBottom: 8,
    },
    counter: {
      fontSize: 12,
      color: '#667085',
      textAlign: 'right',
      marginBottom: 6,
    },
    counterLimit: {
      color: '#B42318',
    },
    field: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        fontSize: 14,
        color: '#101828',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#D0D5DD',
      },
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 8,
      marginTop: 16,
    },
    actionBtn: {
      background: '#ffffff',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      height: 34,
      fontSize: 12,
      fontWeight: 600,
      color: 'rgb(52, 64, 84)',
    },
  })
);

type CampaignNotesProps = {
  campaignId: string;
  notes: string;
  canEdit: boolean;
  onSaved: (campaign: CampaignDetailProps) => void;
};

export function CampaignNotes({
  campaignId,
  notes,
  canEdit,
  onSaved,
}: CampaignNotesProps) {
  const classes = useStyles();
  const { handleError, handleSuccess } = useNotification();
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setExpanded(true);
    setEditing(false);
    setDraft(notes);
  }, [campaignId]);

  useEffect(() => {
    setDraft(notes);
    setEditing(false);
  }, [notes]);

  if (!canEdit && !notes) {
    return null;
  }

  const unchanged = draft.trim() === notes;
  const atLimit = draft.length >= NOTES_MAX_LENGTH;

  const handleSave = async () => {
    if (!canEdit || unchanged || saving) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateCampaignNotes(campaignId, draft);
      onSaved(updated);
      setEditing(false);
      handleSuccess(updated.notes ? 'Notes saved' : 'Notes cleared');
    } catch (error) {
      handleError(error, 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!canEdit || saving || !notes) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateCampaignNotes(campaignId, '');
      onSaved(updated);
      setDraft('');
      setEditing(false);
      handleSuccess('Notes cleared');
    } catch (error) {
      handleError(error, 'Failed to clear notes');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setDraft(notes);
    setEditing(true);
    setExpanded(true);
  };

  return (
    <Box className={classes.root}>
      <Box
        className={clsx(classes.header, expanded && classes.headerOpen)}
        onClick={() => setExpanded((open) => !open)}
        role='button'
        aria-expanded={expanded}
      >
        <span>Notes</span>
        {expanded ? (
          <ExpandLessIcon fontSize='small' />
        ) : (
          <ExpandMoreIcon fontSize='small' />
        )}
      </Box>
      <Collapse in={expanded}>
        <Box className={classes.body}>
          {editing ? (
            <>
              <Typography className={classes.hint}>
                Visible to organizations this campaign is shared with.
              </Typography>
              <Typography
                className={clsx(
                  classes.counter,
                  atLimit && classes.counterLimit
                )}
              >
                {draft.length.toLocaleString()} /{' '}
                {NOTES_MAX_LENGTH.toLocaleString()}
              </Typography>
              <TextField
                className={classes.field}
                value={draft}
                onChange={(event) =>
                  setDraft(event.target.value.slice(0, NOTES_MAX_LENGTH))
                }
                multiline
                minRows={4}
                maxRows={8}
                fullWidth
                variant='outlined'
                placeholder='Add notes for this campaign'
                disabled={saving}
                inputProps={{
                  maxLength: NOTES_MAX_LENGTH,
                  'aria-label': 'Campaign notes',
                }}
              />
            </>
          ) : (
            <Typography className={notes ? classes.notes : classes.empty}>
              {notes || 'No notes'}
            </Typography>
          )}
          {canEdit && (
            <Box className={classes.actions}>
              {editing ? (
                <>
                  <Button
                    className={classes.actionBtn}
                    disabled={saving}
                    onClick={() => {
                      setDraft(notes);
                      setEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className={classes.actionBtn}
                    disabled={unchanged || saving}
                    onClick={handleSave}
                  >
                    {saving ? <Spinner size={12} /> : 'Save'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    className={classes.actionBtn}
                    disabled={saving}
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                  <Button
                    className={classes.actionBtn}
                    disabled={saving || !notes}
                    onClick={handleClear}
                  >
                    {saving ? <Spinner size={12} /> : 'Clear'}
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
